import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { glossaryKeys } from '@/shared/lib/query/queryKeys';
import type { DomainEditHook, DomainTreeNode } from '@/shared/types/editHooks';

import type { DomainHierarchyItem } from '@/entities/domain';
import { useDomainHierarchy } from '@/entities/domain';
import type { GlossaryCategoryItem } from '@/entities/glossary';

import { updateGlossaryCategoryDomains } from '@/features/glossary/api';

const buildDomainTreeFromHierarchy = (items: DomainHierarchyItem[]): DomainTreeNode[] => {
  return items.map((item) => ({
    key: item.id,
    value: item.id,
    title: item.displayName || item.name,
    children: item.children && item.children.length > 0 ? buildDomainTreeFromHierarchy(item.children) : [],
    description: item.description,
    data: item,
  }));
};

type UseGlossaryDomainEditParams = {
  glossaryCategoryId: string;
  categoryName: string;
};

export const useGlossaryDomainEdit = ({
  glossaryCategoryId,
  categoryName,
}: UseGlossaryDomainEditParams): DomainEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);

  const { data: hierarchyData, isLoading: isLoadingDomains } = useDomainHierarchy({ limit: 3000 });

  const glossaryCategoryData = queryClient.getQueryData<GlossaryCategoryItem>(glossaryKeys.categoryInfo(categoryName));
  const currentDomains = useMemo(() => glossaryCategoryData?.domains || [], [glossaryCategoryData?.domains]);
  const currentDomainId = useMemo(() => (currentDomains.length > 0 ? currentDomains[0]?.id : null), [currentDomains]);

  useEffect(() => {
    setSelectedDomainId(currentDomainId);
  }, [currentDomainId]);

  const updateDomainsMutation = useMutation({
    mutationFn: async (selectedDomainId: string | null) => {
      if (!hierarchyData?.data) {
        throw new Error(NOTIFICATION_MESSAGES.DOMAIN_TREE_ERROR);
      }
      await updateGlossaryCategoryDomains(glossaryCategoryId, selectedDomainId, hierarchyData.data, currentDomains);
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: glossaryKeys.categoryInfo(categoryName) });
      notification.error({
        message: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_ERROR,
        description: error instanceof Error ? error.message : NOTIFICATION_MESSAGES.DOMAIN_UPDATE_ERROR_DESC,
        duration: 5,
        placement: 'bottomLeft',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: glossaryKeys.categoryInfo(categoryName) });
      notification.success({
        message: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestCategoryData = queryClient.getQueryData<GlossaryCategoryItem>(glossaryKeys.categoryInfo(categoryName));
    const latestDomains = latestCategoryData?.domains || [];
    const latestDomainId = latestDomains.length > 0 ? latestDomains[0]?.id : null;
    setSelectedDomainId(latestDomainId);
  }, [queryClient, categoryName]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    const latestCategoryData = queryClient.getQueryData<GlossaryCategoryItem>(glossaryKeys.categoryInfo(categoryName));
    const latestDomains = latestCategoryData?.domains || [];
    const latestDomainId = latestDomains.length > 0 ? latestDomains[0]?.id : null;
    setSelectedDomainId(latestDomainId);
  }, [queryClient, categoryName]);

  const handleSelect = useCallback(
    (keys: React.Key[]) => {
      const newKey = keys.length > 0 ? String(keys[0]) : null;

      if (newKey === selectedDomainId) {
        setSelectedDomainId(null);
      } else {
        setSelectedDomainId(newKey);
      }
    },
    [selectedDomainId]
  );

  const handleSave = useCallback(
    async (selectedDomainIds: string[]) => {
      const domainId = selectedDomainIds.length > 0 ? selectedDomainIds[0] : null;
      await updateDomainsMutation.mutateAsync(domainId);
    },
    [updateDomainsMutation]
  );

  const hasChanges = useMemo(() => {
    return currentDomainId !== selectedDomainId;
  }, [currentDomainId, selectedDomainId]);

  const treeData = useMemo(() => {
    if (!hierarchyData?.data) return [];
    return buildDomainTreeFromHierarchy(hierarchyData.data);
  }, [hierarchyData?.data]);

  return {
    isOpen,
    selectedDomainId,
    treeData,
    isLoadingDomains,
    isUpdating: updateDomainsMutation.isPending,
    hasChanges,
    handleOpen,
    handleClose,
    handleSelect,
    handleSave,
  };
};
